import { useMutation } from '@apollo/client';
import { fromJS, Map } from 'immutable';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useReducer } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import AddressBlock from '../../../../components/Base/Address/AddressBlock';
import ErrorMessage from '../../../../components/Base/ErrorMessage/ErrorMessage';
import { SmallTitle } from '../../../../components/Base/Title';
import AddressSelectorContainer from '../../../../containers/AddressSelector';
import { ADD_ADDRESS_BY_REGISTRATION_ID } from '../../../../graphql/mutations';
import { ADDRESSES_LIST, PURCHASE_ADDRESS } from '../../../../graphql/queries';
import { validate } from '../../../../helpers/Validators/addressValidator';
import { useQuery } from '../../../../hooks';
import { useNotification } from '../../../../hooks';
import useDataLayer from '../../../../hooks/useDataLayer';
import useYup from '../../../../hooks/useYup';
import { AuthContext } from '../../../../store/Auth';

const ACTIONS = {
  SET_MODAL_PROPS: 'setModalProps',
  SET_DELIVERY_ERROR_MESSAGE: 'setDeliveryErrorMessage',
  SET_BILLING_ERROR_MESSAGE: 'setBillingErrorMessage',
};

const reducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_MODAL_PROPS:
      return { ...state, modalProps: action.modalProps };
    case ACTIONS.SET_DELIVERY_ERROR_MESSAGE:
      return { ...state, deliveryErrorMessage: action.deliveryErrorMessage };
    case ACTIONS.SET_BILLING_ERROR_MESSAGE:
      return { ...state, billingErrorMessage: action.billingErrorMessage };
    default:
      throw new Error();
  }
};

const Addresses = ({
  registrationId,
  billingAddress = new Map(),
  deliveryAddress = new Map(),
  requiresDeliveryAddress = true,
  setAddressesInvalid,
  setAddressesLoading,
  hasSubmitted,
  refetchPurchaseAddress,
  purchaseRefetch,
}) => {
  const { newNotification } = useNotification();
  const { t } = useTranslation();
  const { isAuthenticated } = useContext(AuthContext);
  const { pushEventLoadedPurchaseFunnel } = useDataLayer();
  const { Yup } = useYup();
  const [state, dispatch] = useReducer(reducer, {
    deliveryErrorMessage: null,
    billingErrorMessage: null,
    modalProps: {
      address: new Map(),
      isBilling: true,
      modalDisplayed: false,
    },
  });

  const {
    data: { addresses = [] },
  } = useQuery(ADDRESSES_LIST, { skip: !isAuthenticated });

  const [addAddressByRegistrationId] = useMutation(ADD_ADDRESS_BY_REGISTRATION_ID, {
    onCompleted: (data) => {
      if (data && data.result.__typename === 'AddAdressCannotBeSaved') {
        data.result.errors.forEach((error) => {
          newNotification({
            content: t(`purchase-summary.error.${error}`),
            type: 'error',
          });
        });
      }

      /* Refreshing the page to display the correct VAT rate if the country of billing is different from the country of purchase */
      if (purchaseRefetch) {
        purchaseRefetch();
      }
      setAddressesLoading(false);
    },
  });

  useEffect(() => {
    refetchPurchaseAddress({ registrationId });
  }, [requiresDeliveryAddress]);

  useEffect(() => {
    validate(billingAddress, Yup)
      .then(() => {
        dispatch({
          type: ACTIONS.SET_BILLING_ERROR_MESSAGE,
          billingErrorMessage: null,
        });
      })
      .catch(() => {
        dispatch({
          type: ACTIONS.SET_BILLING_ERROR_MESSAGE,
          billingErrorMessage: t('common.address.fields.required'),
        });
      });
  }, [billingAddress]);

  useEffect(() => {
    if (!requiresDeliveryAddress) {
      return;
    }

    validate(deliveryAddress, Yup)
      .then(() => {
        dispatch({
          type: ACTIONS.SET_DELIVERY_ERROR_MESSAGE,
          deliveryErrorMessage: null,
        });
      })
      .catch(() => {
        dispatch({
          type: ACTIONS.SET_DELIVERY_ERROR_MESSAGE,
          deliveryErrorMessage: t('common.address.fields.required'),
        });
      });
  }, [deliveryAddress]);

  useEffect(() => {
    if (state.billingErrorMessage || state.deliveryErrorMessage) {
      setAddressesInvalid(true);

      return;
    }

    setAddressesInvalid(false);
  }, [state.deliveryErrorMessage, state.billingErrorMessage]);

  const openAddressModal = (
    { address = new Map(), isBilling = false } = {
      address: new Map(),
      isBilling: false,
    }
  ) => {
    pushEventLoadedPurchaseFunnel({
      type: 'common',
      stepName: 'address',
      stepNumber: 2,
    });
    dispatch({
      type: ACTIONS.SET_MODAL_PROPS,
      modalProps: {
        address,
        isBilling,
        modalDisplayed: true,
      },
    });
  };

  const handleSaveAddress =
    (updateBoth) =>
    ({ address, isBilling, optimisticAddress }) => {
      setAddressesLoading(true);
      dispatch({
        type: ACTIONS.SET_MODAL_PROPS,
        modalProps: {
          isBilling: false,
          address: new Map(),
          modalDisplayed: false,
        },
      });

      return [
        addAddressByRegistrationId({
          variables: {
            registrationId: registrationId,
            address,
            isBilling,
            updateBoth,
          },
          optimisticResponse: {
            result: {
              __typename: 'AddAddressSuccessfullySaved',
              success: true,
            },
          },
          update: (proxy) => {
            const { purchase, ...data } = proxy.readQuery({
              query: PURCHASE_ADDRESS,
              variables: { registrationId },
            });
            proxy.writeQuery({
              query: PURCHASE_ADDRESS,
              variables: { registrationId },
              data: {
                ...data,
                purchase: {
                  ...purchase,
                  billingAddress: isBilling ? optimisticAddress : billingAddress.toJS(),
                  deliveryAddress:
                    requiresDeliveryAddress && (!isBilling || billingAddress.size === 0)
                      ? optimisticAddress
                      : deliveryAddress.toJS(),
                },
              },
            });
          },
        }),
      ];
    };

  const onEditAddress = (address, isBilling) => {
    if (isAuthenticated) {
      dispatch({
        type: ACTIONS.SET_MODAL_PROPS,
        modalProps: {
          isBilling,
          address,
          modalDisplayed: true,
        },
      });
    } else {
      openAddressModal({ address, isBilling });
    }
  };

  const render = ({ deliveryAddress, billingAddress, addressesList }) => {
    return (
      <div className="basket__addresses">
        <SmallTitle>
          <Trans>session-summary.addresses.label</Trans>
        </SmallTitle>
        <div className="basket__addresses-list">
          <div className="basket__addresses-item">
            <AddressBlock
              address={billingAddress}
              cartName={t('session-summary.addresses.title.billing')}
              onEditAddress={(address) => onEditAddress(address, true)}
              addressLabel="session-summary.addresses.title.billing"
              isRequired={true}
              onAddAddress={() => openAddressModal({ isBilling: true })}
            />
            {hasSubmitted && billingAddress.size !== 0 && (
              <ErrorMessage className="basket__addresses-error-message">{state.billingErrorMessage}</ErrorMessage>
            )}
            {hasSubmitted && billingAddress.size === 0 && (
              <ErrorMessage className="basket__addresses-error-message">
                {t('session-summary.purchase.billing-address.required')}
              </ErrorMessage>
            )}
          </div>
          <div className="basket__addresses-item">
            <AddressBlock
              address={deliveryAddress}
              cartName={t('session-summary.addresses.title.shipping')}
              onEditAddress={(address) => onEditAddress(address, false)}
              addressLabel="session-summary.addresses.title.shipping"
              isRequired={requiresDeliveryAddress}
              onAddAddress={() => openAddressModal({ isBilling: false })}
            />
            {hasSubmitted && requiresDeliveryAddress && deliveryAddress.size !== 0 && (
              <ErrorMessage className="basket__addresses-error-message">{state.deliveryErrorMessage}</ErrorMessage>
            )}
            {hasSubmitted && requiresDeliveryAddress && deliveryAddress.size === 0 && (
              <ErrorMessage className="basket__addresses-error-message">
                {t('session-summary.purchase.shipping-address.required')}
              </ErrorMessage>
            )}
          </div>
        </div>
        <AddressSelectorContainer
          isModalDisplayed={state.modalProps.modalDisplayed}
          addressesList={addressesList}
          selectedAddress={state.modalProps.address}
          isLoggedIn={isAuthenticated}
          isBilling={state.modalProps.isBilling}
          onCloseModal={() =>
            dispatch({
              type: ACTIONS.SET_MODAL_PROPS,
              modalProps: { ...state.modalProps, modalDisplayed: false },
            })
          }
          onSaveAddress={handleSaveAddress(requiresDeliveryAddress && billingAddress.size === 0)}
        />
      </div>
    );
  };

  return render({
    deliveryAddress,
    billingAddress,
    addressesList: fromJS(addresses),
  });
};

Addresses.propsType = {
  registrationId: PropTypes.string.isRequired,
  deliveryAddress: PropTypes.instanceOf(Map),
  billingAddress: PropTypes.instanceOf(Map),
  requiresDeliveryAddress: PropTypes.bool,
  refetchPurchaseAddress: PropTypes.func.isRequired,
};

const areEqual = (
  {
    registrationId: prevRegistrationId,
    deliveryAddress: prevDeliveryAddress,
    billingAddress: prevBillingAddress,
    requiresDeliveryAddress: prevRequiresDeliveryAddress,
    hasSubmitted: prevHasSubmitted,
  },
  {
    registrationId: nextRegistrationId,
    deliveryAddress: nextDeliveryAddress,
    billingAddress: nextBillingAddress,
    requiresDeliveryAddress: nextRequiresDeliveryAddress,
    hasSubmitted: nextHasSubmitted,
  }
) =>
  prevRegistrationId === nextRegistrationId &&
  prevDeliveryAddress.equals(nextDeliveryAddress) &&
  prevBillingAddress.equals(nextBillingAddress) &&
  prevRequiresDeliveryAddress === nextRequiresDeliveryAddress &&
  prevHasSubmitted === nextHasSubmitted;

export default React.memo(Addresses, areEqual);
